import React, { useState, useMemo, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import {
	fetchProductLooks,
	fetchProductCollections,
} from '@fh-components/fh-js-api-core/store/slices/productSlice';
import getProductWithoutQuantity from '@/lib/getProductWithoutQuantity';
import { useDispatch } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { Container } from '@/components/styles';
import ecommerce from '@/seo/Ecommerce';
import handleViewport from 'react-in-viewport';
import { Loader, ProductCard, Title } from '@/components';
import { LookImageSliderBlock } from '@/components';
import clsx from 'clsx';

import styles from './Collections.module.scss';

const perPage = 10;

const WideCollection = dynamic(() => import('../WideCollection'), {
	loading: () => <Loader />,
});

const RowSlider = dynamic(() => import('../../../components/rowSlider'), {
	loading: () => <Loader />,
});

const NarrowCollection = ({ title, products, currency }) => {
	if (!products || products.length === 0) return null;

	useEffect(() => {
		if (products && products.length) {
			ecommerce.viewItemList(
				getProductWithoutQuantity(products).map(item => ({
					...item,
					external_id: item.model.slug,
				})),
				currency,
				title,
			);
		}
	}, [products, title]);

	const handleClickCard = (product, index) => {
		const newProducts = getProductWithoutQuantity([product]).map(item => ({
			...item,
			external_id: item.model.slug,
		}));

		ecommerce.viewItemList(newProducts, currency, title, 'select_item', index);
	};

	return (
		<section className={styles.collectionItem}>
			<Container>
				{title && (
					<Title component='h2' className={styles.collectionTitle}>
						{title}
					</Title>
				)}

				<RowSlider arrowsOutside narrowCollection>
					{products.map((product, idx) => {
						return (
							<div
								key={product?.model?.id || idx}
								onClick={() => {
									handleClickCard(product, idx + 1);
								}}
							>
								<ProductCard
									{...product}
									mod='narrowCollection'
									titleLimit
									pageName={title}
									isSafari
									indexCard={idx + 1}
								/>
							</div>
						);
					})}
				</RowSlider>
			</Container>
		</section>
	);
};

const SkeletonContainer = () => {
	const skeletons = Array(4).fill(true);

	return (
		<Container>
			<div className={styles.skeletonTitle}>
				<Skeleton
					key='skeleton-name'
					count={1}
					height='100%'
					width='20%'
					style={{ marginBottom: '24px', marginLeft: '12px' }}
				/>
			</div>
			<div className={styles.skeletonContainer}>
				{skeletons.map((_item, idx) => {
					return (
						<div
							key={idx}
							className={clsx(
								styles.skeleton,
								idx === 2 && styles.skeletonSizeOne,
								idx === 3 && styles.skeletonSizeTwo,
							)}
						>
							<Skeleton
								key={`${idx}-skeleton-shop1`}
								count={1}
								height='76%'
								width='100%'
								style={{ marginBottom: '24px' }}
							/>
							<Skeleton
								key={`${idx}-skeleton-shop2`}
								count={1}
								height='4%'
								width='30%'
								style={{ marginBottom: '8px' }}
							/>
							<Skeleton
								key={`${idx}-skeleton-shop3`}
								count={1}
								height='7%'
								width='100%'
								style={{ marginBottom: '8px' }}
							/>
							<Skeleton
								key={`${idx}-skeleton-shop4`}
								count={1}
								height='4%'
								width='35%'
								style={{ marginBottom: '8px' }}
							/>
						</div>
					);
				})}
			</div>
		</Container>
	);
};

const Block = props => {
	const { forwardedRef } = props;

	return (
		<div ref={forwardedRef}>
			<SkeletonContainer />
		</div>
	);
};

const ViewportBlock = handleViewport(Block);

const Collections = ({ collectionIds, lookId, modelSlug, currency }) => {
	const dispatch = useDispatch();
	const [look, setLook] = useState(null);
	const [collections, setCollections] = useState({});
	const [loadedCollections, setLoadedCollections] = useState({});

	const getLook = useCallback(() => {
		if (lookId) {
			dispatch(
				fetchProductLooks({
					look_id: lookId,
					onSuccess: data => {
						setLook(data?.look?.data || true);
					},
					onError: () => {
						setLook(true);
					},
				}),
			);
		}
	}, [lookId, dispatch]);

	const getCollection = id => {
		if (!loadedCollections[id]) {
			setLoadedCollections(prevState => ({
				...prevState,
				[id]: true,
			}));

			dispatch(
				fetchProductCollections({
					slug: modelSlug,
					page: 1,
					perPage: perPage,
					collection_id: id,
					onSuccess: data => {
						setCollections(prevState => ({
							...prevState,
							[id]: data,
						}));
					},
					onError: () => {
						setCollections(prevState => ({
							...prevState,
							[id]: true,
						}));
					},
				}),
			);
		}
	};

	const lookBlock = useMemo(() => {
		if (lookId) {
			if (look === true) {
				return null;
			}

			if (look) {
				return (
					<div className={styles.looks}>
						<LookImageSliderBlock {...look} productPage />
					</div>
				);
			} else {
				return (
					<div className={collectionIds.length > 0 && styles.skeletonLookViewport}>
						<ViewportBlock
							onEnterViewport={() => {
								getLook();
							}}
						/>
					</div>
				);
			}
		}

		return null;
	}, [lookId, look, getLook]);

	return (
		<>
			{lookBlock}
			{collectionIds.map(id => {
				const collection = collections[id];

				if (collection === true) {
					return null;
				}

				if (collection) {
					const {
						type,
						title: collectionTitle,
						products,
						full_row,
						pages: pagesCount,
						id,
					} = collection;

					switch (type) {
						case 'wide': {
							return (
								<div className={styles.collectionItem}>
									<WideCollection
										id={id}
										key={collectionTitle}
										title={collectionTitle}
										products={products}
										full_row={full_row}
										currency={currency}
										pagesCount={pagesCount}
										perPage={perPage}
										slug={modelSlug}
										SkeletonContainer={SkeletonContainer}
									/>
								</div>
							);
						}
						case 'narrow': {
							return (
								<div className={styles.collectionItem}>
									<NarrowCollection
										key={collectionTitle}
										title={collectionTitle}
										products={products}
										currency={currency}
									/>
								</div>
							);
						}
						default:
							return null;
					}
				}

				return (
					<div className={styles.skeletonCollectionViewport}>
						<ViewportBlock
							onEnterViewport={() => {
								getCollection(id);
							}}
						/>
					</div>
				);
			})}
		</>
	);
};

export default Collections;
